import React, { useEffect, useState } from 'react';

import { Box, Button, Typography, useTheme } from '@mui/material';
import { CSSProperties } from '@mui/material/styles/createMixins';
import Collapsible from 'components/collapsible';
import FlexBox from 'components/flex-box';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';
import { useGet } from 'hooks/use-get';
import { XOctagon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getPonds } from 'views/production-systems/api/productionSystems';
import Active from 'views/production-systems/assets/Active';
import Inactive from 'views/production-systems/assets/Inactive';
import { usePondsState } from 'views/production-systems/context/ponds-context';
import { PondStatus } from 'views/production-systems/types/pond.type';

type LockPondContainerProps = {
  setConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  handleClose?: () => void;
  canContinue?: boolean;
  fromAdd?: boolean;
};

// Should we pass a dialog ref to lock pond container, and have the dialog contain an onClose function call to release ponds?
// Doing this means every container would need an optional prop 'dialogRef' because the containers will not always be in a dialog
// The goal is to reduce the bloat of passed props

const PAGE_NAME_ID = `productionSystems.lockPondContainer`;

const LockPondContainer: React.FC<LockPondContainerProps> = ({ setConfirm, handleClose, fromAdd = true, canContinue = true }) => {
  const theme = useTheme();
  const intl = useIntl();
  const { lockedPonds, globalSiteId, registrationDate } = usePondsState();
  const [allowContinue, setAllowContinue] = useState<boolean>(canContinue);

  const { data: pondState } = useGet(['getPonds', globalSiteId, registrationDate.format('YYYY-MM-DD')], ({ queryKey }) =>
    getPonds(String(queryKey[1]), String(queryKey[2]))
  );

  const getIcon = (type: boolean, sx?: CSSProperties) => {
    return type ? <Active sx={sx} /> : <Inactive style={sx} />;
  };

  useEffect(() => {
    if (lockedPonds.granted.length === 0 && fromAdd) {
      setAllowContinue(false);
    }
  }, [lockedPonds]);

  return (
    <>
      {!!lockedPonds && (
        <FlexBox>
          <Box
            padding={4}
            sx={{ maxWidth: 500, display: 'flex', alignItems: 'center', borderRadius: 4, backgroundColor: theme.palette.red.o50 }}>
            <XOctagon size={80} />
            {lockedPonds.granted?.length > 0 ? (
              <Typography ml={8}>
                <b>{intl.formatMessage({ id: `${PAGE_NAME_ID}.partialLock.bold` })}</b>
                {' ' + intl.formatMessage({ id: `${PAGE_NAME_ID}.partialLock.message` })}
              </Typography>
            ) : (
              <Typography ml={8}>
                <b>{intl.formatMessage({ id: `${PAGE_NAME_ID}.fullLock.bold` })}</b>
                {!fromAdd && ' ' + intl.formatMessage({ id: `${PAGE_NAME_ID}.fullLock.message` })}
              </Typography>
            )}
          </Box>
          <Box>
            <Collapsible
              sx={{
                ['.MuiTableCell-body']: {
                  background: `${theme.palette.darkBlue.o25} !important`,
                },
                ['.Collapsible-default']: {
                  background: '#F5C4CD',
                  borderRadius: 2,
                },
                ['.Collapsible-open']: {
                  background: '#F5C4CD',
                  borderRadius: '8px 8px 0 0',
                },
              }}
              open={!!lockedPonds.notGranted.length}
              label={intl.formatMessage({ id: `${PAGE_NAME_ID}.notGrantedLabel` })}>
              <List dark>
                {pondState?.ponds
                  ?.filter((pond) => lockedPonds.notGranted?.includes(pond.pondId))
                  .map((pond) => (
                    <ListRow key={pond.pondId} className={lockedPonds.notGranted.includes(pond.pondId) ? 'Mui-disabled' : ''}>
                      <ListCell>{pond.pondName}</ListCell>
                      <ListCell>{pond.zoneName}</ListCell>
                      <ListCell>{getIcon(pond.pondStatus === PondStatus.active, { verticalAlign: 'middle' })}</ListCell>
                      <ListCell>{pond.activeInactiveDays}</ListCell>
                    </ListRow>
                  ))}
              </List>
            </Collapsible>
          </Box>
          <Box>
            <Collapsible open={!!lockedPonds.granted?.length} label={intl.formatMessage({ id: `${PAGE_NAME_ID}.grantedLabel` })}>
              <List dark>
                {pondState?.ponds
                  ?.filter((pond) => lockedPonds.granted?.includes(pond.pondId))
                  .map((pond) => (
                    <ListRow key={pond.pondId}>
                      <ListCell>{pond.pondName}</ListCell>
                      <ListCell>{pond.zoneName}</ListCell>
                      <ListCell>{getIcon(pond.pondStatus === PondStatus.active, { verticalAlign: 'middle' })}</ListCell>
                      <ListCell>{pond.activeInactiveDays}</ListCell>
                    </ListRow>
                  ))}
              </List>
            </Collapsible>
          </Box>
          <Box display={'flex'} justifyContent={canContinue ? 'flex-end' : 'space-between'}>
            {!canContinue && (
              <Button variant="outlined" onClick={handleClose}>
                <FormattedMessage id="general.cancel" />
              </Button>
            )}
            <Button onClick={() => setConfirm((prev) => !prev)} disabled={!allowContinue}>
              <FormattedMessage id="general.next" />
            </Button>
          </Box>
        </FlexBox>
      )}
    </>
  );
};

export default LockPondContainer;
